/**
 * 2.0 wave 2, W2-CHROME (spec 7.5, CH-2, CH-3, CH-5) — the View switch.
 *
 * One group, "View", with one button per option the account has: Training,
 * Coaching, Guardian (Appendix C 45 to 48). It sits in the avatar menu, in the
 * native avatar sheet and in the desktop side nav (where the old "Switch to
 * guardian view" row was), and it renders only when the account has TWO or
 * more options: a one-role account sees no View group at all.
 *
 * Choosing an option stores the view for this user on this device, which swaps
 * the whole tab bar and rail at once (every reader shares one store), and
 * navigates to the view's first tab: `/today`, `/team` or `/guardian`.
 * Choosing the view the account is already in only closes the menu.
 *
 * No request is made and no authorization changes (CH-11): the switch writes a
 * device-local value that `useAccountView` validates against the account's own
 * roles before it ever reads it.
 *
 * `variant="menu"` is for the popover and sheet (`role="menu"`, so the options
 * are `menuitemradio`); `variant="rail"` is for the desktop side nav.
 */

import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountView } from '../../../hooks/useAccountView';
import { ACCOUNT_VIEW_LABELS, type AccountViewId } from '../../../lib/accountView';
import { landingPathForView } from '../../../lib/nav/tabSets';
import { Icon } from '../../icons/registry';
import styles from './ViewSwitch.module.css';

export interface ViewSwitchProps {
  variant: 'menu' | 'rail';
  /** Called after a choice, so the menu or sheet can close. */
  onChosen?: () => void;
}

export function ViewSwitch({ variant, onChosen }: ViewSwitchProps): JSX.Element | null {
  const { view, options, setView } = useAccountView();
  const navigate = useNavigate();
  const labelId = useId();

  if (options.length < 2) return null;

  const choose = (next: AccountViewId): void => {
    if (next !== view) {
      setView(next);
      navigate(landingPathForView(next));
    }
    onChosen?.();
  };

  const isMenu = variant === 'menu';
  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className={[styles.group, isMenu ? styles.groupMenu : styles.groupRail].join(' ')}
    >
      <div id={labelId} className={styles.label}>
        View
      </div>
      {options.map((option) => {
        const on = option === view;
        return (
          <button
            key={option}
            type="button"
            className={[styles.option, on ? styles.optionOn : ''].filter(Boolean).join(' ')}
            {...(isMenu
              ? { role: 'menuitemradio' as const, 'aria-checked': on }
              : { 'aria-pressed': on })}
            onClick={() => choose(option)}
          >
            <span>{ACCOUNT_VIEW_LABELS[option]}</span>
            {on ? (
              <span className={styles.check} aria-hidden="true">
                <Icon name="sys.check" size={16} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
